import { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTranslation } from 'react-i18next';
import DeleteAccountMutation from '../data/mutations/DeleteAccountMutation';
import { logout } from '../data/client';

const CONFIRM_WORD = 'RADERA';

type Step = 'retention' | 'info' | 'reason' | 'confirm';

type ReasonKey =
  | 'met_someone'
  | 'dislike_the_app'
  | 'disbelieve_concept'
  | 'not_working'
  | 'dislike_dating_apps'
  | 'legacy_dating_app_sunset';

type Props = {
  open: boolean;
  onClose: () => void;
};

export default function DeleteAccountModal({ open, onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('retention');
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState<ReasonKey | ''>('');
  const [metSomeoneVia, setMetSomeoneVia] = useState<string>('');
  const [motivation, setMotivation] = useState('');

  const [deleteAccount, { loading }] = useMutation(DeleteAccountMutation);

  useEffect(() => {
    if (open) {
      setStep('retention');
      setConfirmText('');
      setError(null);
      setReason('');
      setMetSomeoneVia('');
      setMotivation('');
    }
  }, [open]);

  const handleJoinNetwork = useCallback(() => {
    onClose();
    navigate('/application?prefill=true');
  }, [navigate, onClose]);

  const handleDelete = useCallback(async () => {
    setError(null);
    const feedback: { reason: string; motivation?: string; extra?: string[] } = {
      reason: reason || 'legacy_dating_app_sunset',
    };
    if (reason === 'met_someone' && metSomeoneVia) {
      feedback.motivation = metSomeoneVia;
    }
    if (motivation.trim()) {
      feedback.motivation = feedback.motivation
        ? `${feedback.motivation}: ${motivation.trim()}`
        : motivation.trim();
    }
    try {
      const { data } = await deleteAccount({ variables: { feedback } });
      if (data?.delete?.deleted) {
        await logout();
        onClose();
        navigate('/');
      } else {
        setError(t('delete.error_generic', 'Något gick fel. Försök igen.'));
      }
    } catch (err) {
      setError(t('delete.error_generic', 'Något gick fel. Försök igen.'));
    }
  }, [deleteAccount, navigate, onClose, t, reason, metSomeoneVia, motivation]);

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          backgroundColor: '#38191E',
          color: '#fff',
          backgroundImage: 'none',
          borderRadius: 3,
        },
      }}
    >
      {step === 'retention' && (
        <>
          <DialogTitle sx={{ fontWeight: 700, pt: 3.5, px: 3.5, pb: 1 }}>
            {t('delete.retention_title', 'Innan du går — gå med i nätverket?')}
          </DialogTitle>
          <DialogContent sx={{ px: 3.5, pb: 1 }}>
            <Typography variant="body2" sx={{ opacity: 0.85, mb: 2 }}>
              {t(
                'delete.retention_body',
                'Dejtingappen stängs ner, men du kan fortsätta din resa i Relates matchmaking-nätverk. Våra matchmakers hjälper dig hitta personer som passar dig på riktigt — inga fler slumpmässiga swipes.',
              )}
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, opacity: 0.8, m: 0 }}>
              <li>
                <Typography variant="body2">
                  {t('delete.retention_point_2', 'Våra matchmakers matchar dig')}
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  {t('delete.retention_point_1', 'Du behåller alla dina konversationer')}
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  {t('delete.retention_point_3', 'Medlemskap i nätverket är kostnadsfritt')}
                </Typography>
              </li>
            </Box>
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, px: 3.5, pt: 2, pb: 3 }}>
            <Button
              variant="contained"
              onClick={handleJoinNetwork}
              fullWidth
              size="large"
            >
              {t('delete.retention_cta_join', 'Gå med i nätverket')}
            </Button>
            <Button
              onClick={() => setStep('info')}
              fullWidth
              size="large"
              sx={{ color: 'inherit', opacity: 0.7, textTransform: 'none' }}
            >
              {t('delete.retention_cta_continue', 'Nej tack, radera mitt konto')}
            </Button>
          </DialogActions>
        </>
      )}

      {step === 'info' && (
        <>
          <DialogTitle sx={{ fontWeight: 700, pt: 3.5, px: 3.5, pb: 1 }}>
            {t('delete.info_title', 'Det här händer om du raderar')}
          </DialogTitle>
          <DialogContent sx={{ px: 3.5, pb: 1 }}>
            <Typography variant="body2" sx={{ opacity: 0.85, mb: 1.5 }}>
              {t('delete.info_body', 'När du raderar ditt konto tas följande bort permanent:')}
            </Typography>
            <Box component="ul" sx={{ pl: 2.5, opacity: 0.85, m: 0, mb: 2 }}>
              <li><Typography variant="body2">{t('delete.info_point_profile', 'Din profil, bilder och värderingar')}</Typography></li>
              <li><Typography variant="body2">{t('delete.info_point_matches', 'Alla matchningar')}</Typography></li>
              <li><Typography variant="body2">{t('delete.info_point_chats', 'All chatthistorik')}</Typography></li>
            </Box>
            <Typography
              variant="body2"
              sx={{ color: '#ff8a8a', fontWeight: 700, display: 'block', mb: 1.5 }}
            >
              {t('delete.info_irreversible', 'Detta går inte att ångra.')}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.6, display: 'block' }}>
              {t(
                'delete.info_data_export',
                'Vill du ha ut din data innan du raderar? Mejla oss på hello@relate-matchmaking.com.',
              )}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, px: 3.5, pt: 2, pb: 3 }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={onClose}
            >
              {t('delete.cancel_keep', 'Avbryt, behåll mitt konto')}
            </Button>
            <Button
              onClick={() => setStep('reason')}
              size="large"
              fullWidth
              sx={{ color: '#ff8a8a', opacity: 0.85, textTransform: 'none' }}
            >
              {t('delete.continue_to_confirm', 'Fortsätt ändå')}
            </Button>
          </DialogActions>
        </>
      )}

      {step === 'reason' && (
        <>
          <DialogTitle sx={{ fontWeight: 700, pt: 3.5, px: 3.5, pb: 1 }}>
            {t('delete.reason_title', 'Varför lämnar du?')}
          </DialogTitle>
          <DialogContent sx={{ px: 3.5, pb: 1 }}>
            <Typography variant="body2" sx={{ opacity: 0.85, mb: 1.5 }}>
              {t('delete.reason_body', 'Din feedback hjälper oss bli bättre.')}
            </Typography>
            <RadioGroup
              value={reason}
              onChange={(e) => {
                setReason(e.target.value as ReasonKey);
                if (e.target.value !== 'met_someone') setMetSomeoneVia('');
              }}
            >
              {[
                { key: 'met_someone', label: t('delete.reason.met_someone', 'Jag har träffat någon') },
                { key: 'dislike_the_app', label: t('delete.reason.dislike_the_app', 'Jag gillar inte Relate') },
                { key: 'disbelieve_concept', label: t('delete.reason.disbelieve_concept', 'Jag tror inte på konceptet') },
                { key: 'not_working', label: t('delete.reason.not_working', 'Appen fungerar inte') },
                { key: 'dislike_dating_apps', label: t('delete.reason.dislike_dating_apps', 'Jag vill inte använda datingappar') },
                { key: 'other', label: t('delete.reason.other', 'Annat') },
              ].map(opt => (
                <FormControlLabel
                  key={opt.key}
                  value={opt.key}
                  control={<Radio sx={{ color: '#b89196', '&.Mui-checked': { color: '#fff' } }} />}
                  label={<Typography variant="body2">{opt.label}</Typography>}
                  sx={{ my: 0 }}
                />
              ))}
            </RadioGroup>
            {reason === 'met_someone' && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ opacity: 0.75, mb: 0.5, display: 'block' }}>
                  {t('delete.reason.met_where', 'Var träffades ni?')}
                </Typography>
                <RadioGroup
                  row
                  value={metSomeoneVia}
                  onChange={(e) => setMetSomeoneVia(e.target.value)}
                >
                  {[
                    { key: 'relate_app', label: t('delete.reason.met_via_relate', 'Via Relate') },
                    { key: 'other_apps', label: t('delete.reason.met_via_other_apps', 'Annan app') },
                    { key: 'other_ways', label: t('delete.reason.met_via_other_ways', 'Annat') },
                  ].map(opt => (
                    <FormControlLabel
                      key={opt.key}
                      value={opt.key}
                      control={<Radio size="small" sx={{ color: '#b89196', '&.Mui-checked': { color: '#fff' } }} />}
                      label={<Typography variant="caption">{opt.label}</Typography>}
                    />
                  ))}
                </RadioGroup>
              </Box>
            )}
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={motivation}
              onChange={(e) => setMotivation(e.target.value)}
              placeholder={t('delete.reason.motivation_placeholder', 'Vill du berätta mer? (valfritt)')}
              sx={{
                mt: 1.5,
                '.MuiInputBase-root': {
                  backgroundColor: '#4A292E',
                  color: '#fff',
                  borderRadius: 2,
                  fontSize: 14,
                },
                '.MuiOutlinedInput-notchedOutline': { border: 0 },
              }}
            />
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, px: 3.5, pt: 2, pb: 3 }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={onClose}
            >
              {t('delete.cancel_keep', 'Avbryt, behåll mitt konto')}
            </Button>
            <Button
              onClick={() => setStep('confirm')}
              size="large"
              fullWidth
              disabled={!reason}
              sx={{
                color: '#ff8a8a',
                opacity: reason ? 0.85 : 0.4,
                textTransform: 'none',
              }}
            >
              {t('delete.continue_to_confirm', 'Fortsätt ändå')}
            </Button>
          </DialogActions>
        </>
      )}

      {step === 'confirm' && (
        <>
          <DialogTitle sx={{ fontWeight: 700, pt: 3.5, px: 3.5, pb: 1 }}>
            {t('delete.confirm_title', 'Bekräfta radering')}
          </DialogTitle>
          <DialogContent sx={{ px: 3.5, pb: 1 }}>
            <Typography variant="body2" sx={{ opacity: 0.85, mb: 2 }}>
              {t('delete.confirm_body', {
                word: CONFIRM_WORD,
                defaultValue: 'Skriv {{word}} för att bekräfta.',
              })}
            </Typography>
            <TextField
              fullWidth
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={loading}
              placeholder={CONFIRM_WORD}
              sx={{
                '.MuiInputBase-root': {
                  backgroundColor: '#4A292E',
                  color: '#fff',
                  borderRadius: 2,
                },
                '.MuiOutlinedInput-notchedOutline': { border: 0 },
              }}
            />
            {error && (
              <Typography variant="caption" sx={{ color: '#ff8a8a', mt: 1, display: 'block' }}>
                {error}
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, px: 3.5, pt: 2, pb: 3 }}>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={onClose}
              disabled={loading}
            >
              {t('delete.cancel_keep', 'Avbryt, behåll mitt konto')}
            </Button>
            <Button
              onClick={handleDelete}
              size="large"
              fullWidth
              disabled={confirmText.trim().toUpperCase() !== CONFIRM_WORD || loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{
                color: '#ff8a8a',
                opacity: confirmText.trim().toUpperCase() !== CONFIRM_WORD || loading ? 0.4 : 0.85,
                textTransform: 'none',
              }}
            >
              {t('delete.confirm_cta', 'Radera mitt konto')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
